import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export interface WrongQuestion {
    id: number;
    categoryId: number;
    type: 'qcm' | 'vrai_faux';
    date: string;
}

/**
 * Tracks a question as wrong or removes it if now correct.
 * Returns the updated user object or null if no update was performed.
 */
export const trackAnswer = async (questionId: number, categoryId: number, type: 'qcm' | 'vrai_faux', isCorrect: boolean) => {
    try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        let wrongQuestions: WrongQuestion[] = [...(user.user_metadata.wrong_questions || [])];

        const existingIndex = wrongQuestions.findIndex(q => q.id === questionId && q.type === type);

        let modified = false;
        if (isCorrect) {
            if (existingIndex !== -1) {
                // Remove if it was wrong before
                wrongQuestions.splice(existingIndex, 1);
                modified = true;
            } else {
                // Not in the wrong list and current answer is correct, nothing to do
                return user;
            }
        } else {
            if (existingIndex === -1) {
                // Add if it's a new wrong answer
                wrongQuestions.push({
                    id: questionId,
                    categoryId,
                    type,
                    date: new Date().toISOString()
                });
                modified = true;
            } else {
                // Already in list, just update date
                wrongQuestions[existingIndex] = {
                    ...wrongQuestions[existingIndex],
                    date: new Date().toISOString()
                };
                modified = true;
            }
        }

        if (modified) {
            const { data: { user: updatedUser }, error } = await supabase.auth.updateUser({
                data: {
                    ...user.user_metadata,
                    wrong_questions: wrongQuestions
                }
            });
            if (error) throw error;
            return updatedUser;
        }

        return user;
    } catch (error) {
        console.error('Error tracking answer:', error);
        return null;
    }
};

/**
 * Gets the count of wrong questions.
 */
export const getWrongQuestionsCount = (user: any, categoryId?: number) => {
    const wrongQuestions: WrongQuestion[] = user?.user_metadata?.wrong_questions || [];
    if (categoryId) {
        return wrongQuestions.filter(q => q.categoryId === categoryId).length;
    }
    return wrongQuestions.length;
};
